import React, { useEffect, useRef, useState } from 'react';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { trackEvent } from '../../../utils/analytics';
import { createLead } from '../../../services/leadApi';

/**
 * RoleContactModal — the secondary "Talk to us" lead-capture modal on the
 * AI-Employee role pages. Submits to OUR internal leads API (api/lead.py):
 * leads are stored in Mongo, admins see them at /admin/leads, and every
 * CellCog admin gets an email notification. (Replaced the external
 * Web3Forms dependency, 2026-07-04.)
 *
 * Keeps the honeypot field: the backend silently drops submissions where
 * botcheck is filled. Fires GA4 `generate_lead` on success (Key Event,
 * see PRD §6).
 */

const RoleContactModal = ({ open, onClose, roleDisplay, dark = true }) => {
  const [form, setForm] = useState({ name: '', email: '', message: '', botcheck: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const dialogRef = useRef(null);

  // Escape-to-close + scroll lock while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  // Reset to a fresh form the next time it opens after a send.
  useEffect(() => {
    if (open && status === 'sent') {
      setStatus('idle');
      setForm({ name: '', email: '', message: '', botcheck: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await createLead({
        name: form.name,
        email: form.email,
        message: form.message,
        role: roleDisplay,
        source_page: window.location.pathname,
        botcheck: form.botcheck || undefined, // backend silently drops if filled
      });
      if (res.data?.success) {
        trackEvent('generate_lead', { role: roleDisplay, source: 'role_page_modal' });
        setStatus('sent');
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <div
      className={`rolp-modal__overlay ${dark ? 'rolp-modal--dark' : 'rolp-modal--light'}`}
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="rolp-modal" role="dialog" aria-modal="true" aria-labelledby="rolp-modal-title" ref={dialogRef}>
        <button type="button" className="rolp-modal__close" aria-label="Close" onClick={onClose}>
          <CloseOutlined />
        </button>

        {status === 'sent' ? (
          <div className="rolp-modal__thanks">
            <span className="rolp-modal__tick"><CheckOutlined /></span>
            <h3>You are on our radar</h3>
            <p>Thanks for your interest in an {roleDisplay}. Our team will be in touch within 2 business days.</p>
          </div>
        ) : (
          <>
            <div className="rolp-modal__head">
              <h3 id="rolp-modal-title">Talk to us about your {roleDisplay}</h3>
              <p>Tell us what you need and our team will help you set up your CellCog AI Employee.</p>
            </div>
            <form className="rolp-modal__form" onSubmit={submit}>
              <input
                className="rolp-modal__hp"
                type="text"
                value={form.botcheck}
                onChange={set('botcheck')}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <label htmlFor="rolp-cf-name">Name</label>
              <input id="rolp-cf-name" type="text" required placeholder="Your name" value={form.name} onChange={set('name')} />
              <label htmlFor="rolp-cf-email">Email</label>
              <input id="rolp-cf-email" type="email" required placeholder="you@company.com" value={form.email} onChange={set('email')} />
              <label htmlFor="rolp-cf-message">What do you need?</label>
              <textarea id="rolp-cf-message" required rows={4} placeholder="Tell us about the work you want covered" value={form.message} onChange={set('message')} />
              <button type="submit" className="rolp-modal__submit" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : 'Send message'}
              </button>
              {status === 'error' && (
                <p className="rolp-modal__error">Something went wrong. Email us at support@cellcog.ai instead.</p>
              )}
              <p className="rolp-modal__note">We will never share your details.</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default RoleContactModal;
